<script setup lang="ts">
import { computed } from 'vue'
import { TrophyIcon } from '@heroicons/vue/24/solid'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'

type Palier = { montant: number; prime: number }
type Booster = { marge: number; prime: number }

const props = defineProps<{
  paliers: Palier[]
  teamBooster: Booster[]
  current: number
  objectif: number
  year: number
  reloadable?: boolean
}>()

const emit = defineEmits<{ (e: 'reload'): void }>()

const fmtEur = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} €`
const fmtK = (n: number) => `${(n / 1000).toLocaleString('fr-FR')} K€`

const isReached = (montant: number) => props.current >= montant

// Interpolation de couleur teal -> orange selon la position (comme la bande du PDF).
function lerpColor(t: number): string {
  const from = [45, 212, 191] // teal-400
  const to = [249, 115, 22] // orange-500
  const c = from.map((f, i) => Math.round(f + (to[i] - f) * t))
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`
}
const bubbleColor = (i: number) => {
  const t = props.paliers.length > 1 ? i / (props.paliers.length - 1) : 0
  return `linear-gradient(135deg, ${lerpColor(t)}, ${lerpColor(Math.min(t + 0.15, 1))})`
}
const boosterColor = (i: number) => {
  const t = props.teamBooster.length > 1 ? i / (props.teamBooster.length - 1) : 0
  return `linear-gradient(135deg, ${lerpColor(t)}, ${lerpColor(Math.min(t + 0.2, 1))})`
}

// Décalage vertical pour l'effet "ascension" (escalier montant de gauche à droite).
const climbOffset = (i: number) => (props.paliers.length > 1 ? (i / (props.paliers.length - 1)) * 80 : 0)

const palierActuel = computed(() => [...props.paliers].reverse().find((p) => isReached(p.montant)) ?? null)
const prochainPalier = computed(() => props.paliers.find((p) => !isReached(p.montant)) ?? null)
const resteProchain = computed(() => (prochainPalier.value ? Math.max(prochainPalier.value.montant - props.current, 0) : 0))
</script>

<template>
  <div class="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
    <!-- En-tête -->
    <div class="flex justify-between items-start mb-2 flex-wrap gap-4">
      <div>
        <div class="text-xs uppercase tracking-wider font-semibold text-gray-500">Plan de commissionnement — ONE {{ year }}</div>
        <div class="flex items-baseline gap-3 mt-1">
          <span class="text-3xl font-extrabold text-gray-900">{{ fmtEur(current) }}</span>
          <span v-if="palierActuel" class="px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm" :style="{ background: bubbleColor(paliers.indexOf(palierActuel)) }">
            Prime palier : {{ fmtK(palierActuel.prime) }}
          </span>
        </div>
        <div class="text-gray-500 text-sm uppercase tracking-wider font-semibold">Marge cumulée (année civile)</div>
      </div>
      <div class="flex flex-col items-end gap-2">
        <button
          v-if="reloadable"
          type="button"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
          title="Recalculer le variable"
          @click="emit('reload')"
        >
          <ArrowPathIcon class="w-4 h-4" /> Recalculer
        </button>
        <div class="text-right">
          <span class="px-3 py-1 rounded-full text-xs font-bold text-white shadow" :style="{ background: 'linear-gradient(135deg,#2dd4bf,#f97316)' }">
            OBJECTIF MARGE
          </span>
          <div class="text-xl font-extrabold text-gray-800 mt-1">{{ fmtEur(objectif) }}</div>
        </div>
      </div>
    </div>

    <!-- Escalier ascendant des paliers (visuel inspiré du payplan) -->
    <div class="relative overflow-x-auto w- pt-4 pb-4">
      <!-- ligne de pente -->
      <div class="absolute inset-x-6 bottom-8 h-1 rounded-full opacity-70"
      :style="{ background: 'linear-gradient(90deg,#2dd4bf,#f97316)', transform: 'rotate(-3.8deg)', transformOrigin: 'left bottom' }"></div>
      <div class="flex items-end gap-2 md:gap-3 min-w-max px-2 relative justify-around">
        <div
          v-for="(p, i) in paliers"
          :key="i"
          class="flex flex-col items-center justify-end"
          :style="{ marginBottom: `${climbOffset(i)}px` }"
        >
          <!-- prime -->
          <div class="text-sm font-extrabold mb-1 whitespace-nowrap" :class="isReached(p.montant) ? 'text-gray-900' : 'text-gray-400'">
            {{ fmtK(p.prime) }}
          </div>
          <!-- bulle marge -->
          <div
            class="px-3 py-1.5 rounded-full text-xs font-bold text-white whitespace-nowrap shadow-sm transition-all duration-500"
            :class="isReached(p.montant) ? 'scale-105' : 'opacity-40 grayscale'"
            :style="{ background: bubbleColor(i) }"
          >
            {{ fmtEur(p.montant) }}
          </div>
          <!-- tige + pastille -->
          <div class="w-0.5 h-4" :class="isReached(p.montant) ? 'bg-emerald-400' : 'bg-gray-200'"></div>
          <div
            class="w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center"
            :class="isReached(p.montant) ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300'"
          ></div>
        </div>
      </div>
    </div>

    <!-- Reste à parcourir -->
    <div class="flex justify-end text-sm pt-2">
      <div v-if="prochainPalier" class="text-gray-500">
        Plus que
        <span class="text-orange-600 font-bold text-lg">{{ fmtEur(resteProchain) }}</span>
        pour débloquer une prime de
        <span class="text-gray-900 font-bold">{{ fmtK(prochainPalier.prime) }}</span>
      </div>
      <div v-else class="text-emerald-600 font-bold flex items-center gap-2">
        <TrophyIcon class="w-5 h-5" /> Objectif marge atteint ! Félicitations !
      </div>
    </div>

    <!-- Booster Team Performance -->
    <div v-if="teamBooster.length" class="mt-6 border-t border-gray-100 pt-4">
      <div class="inline-block px-4 py-1.5 rounded-lg text-sm font-bold text-white mb-3" :style="{ background: 'linear-gradient(135deg,#2dd4bf,#22d3ee)' }">
        Booster Team Performance
      </div>
      <p class="text-xs text-gray-500 mb-3">Prime d'équipe déclenchée à l'atteinte de la marge cumulée de la BU.</p>
      <div class="flex flex-wrap gap-4">
        <div v-for="(b, i) in teamBooster" :key="i" class="flex items-center gap-2">
          <span class="px-3 py-1.5 rounded-lg text-xs font-bold text-white shadow-sm" :style="{ background: boosterColor(i) }">
            &gt; {{ fmtEur(b.marge) }}
          </span>
          <span class="px-3 py-1.5 rounded-lg text-xs font-extrabold text-white shadow-sm" :style="{ background: boosterColor(i) }">
            {{ fmtK(b.prime) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
